import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Dropdown, Icon, Loader, Message, Modal, Table } from 'semantic-ui-react';
import {
  ContractListDto,
  ContractResponseDto,
  contractService,
  EContractStatus,
  RestoreCancelledContractsResultDto,
} from '../../services/contracts/ContractService';
import {
  getRestoreWindowExpiredReason,
  getRestoreWindowStartMs,
  mapWithConcurrencyLimit,
  RESTORE_PARALLELISM_LIMIT,
} from '../../utils/contractRestoreUtils';
import { getContractStatusText, normalizeContractStatus } from '../../utils/contractStatusUtils';
import { formatCnpj } from '../../services/utils/stringUtils';

type RollbackTarget = {
  contract: ContractListDto;
  previousStatus: EContractStatus | null;
  selectedStatus: EContractStatus | null;
  eligible: boolean;
  reason?: string;
};

type LinePreview = {
  contractId: string;
  lineId: string;
  chipCcid: string | null;
  ddd: string | null;
  numero: string | null;
};

interface ContractRollbackPreviewModalProps {
  open: boolean;
  selectedContracts: ContractListDto[];
  isAdmin?: boolean;
  onClose: () => void;
  onCompleted: (result: RestoreCancelledContractsResultDto) => void;
}

const TEAM_LEADER_BASE_RESTORE_STATUSES: EContractStatus[] = [
  EContractStatus.Criado,
  EContractStatus.Assinado,
];

const allRestoreStatusOptions = Object.values(EContractStatus)
  .filter((value): value is EContractStatus => typeof value === 'number')
  .sort((a, b) => a - b)
  .map((status) => ({
    key: status,
    value: status,
    text: getContractStatusText(status),
  }));

function buildRestoreStatusOption(status: EContractStatus) {
  return { key: status, value: status, text: getContractStatusText(status) };
}

function getAllowedRestoreStatuses(previousStatus: EContractStatus | null, isAdmin: boolean): EContractStatus[] {
  if (isAdmin) return allRestoreStatusOptions.map((o) => o.value);
  const allowed = new Set<EContractStatus>(TEAM_LEADER_BASE_RESTORE_STATUSES);
  if (previousStatus != null) allowed.add(previousStatus);
  return Array.from(allowed).sort((a, b) => a - b);
}

function getRestoreStatusOptions(previousStatus: EContractStatus | null, isAdmin: boolean) {
  return getAllowedRestoreStatuses(previousStatus, isAdmin).map(buildRestoreStatusOption);
}

function resolveInitialRestoreStatus(previousStatus: EContractStatus | null, isAdmin: boolean): EContractStatus | null {
  const allowed = getAllowedRestoreStatuses(previousStatus, isAdmin);
  if (previousStatus != null && allowed.includes(previousStatus)) return previousStatus;
  if (allowed.includes(EContractStatus.Criado)) return EContractStatus.Criado;
  return allowed[0] ?? null;
}

export const ContractRollbackPreviewModal: React.FC<ContractRollbackPreviewModalProps> = ({
  open,
  selectedContracts,
  isAdmin = false,
  onClose,
  onCompleted,
}) => {
  const [step, setStep] = useState<1 | 2 | 3 | 'result'>(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [targets, setTargets] = useState<RollbackTarget[]>([]);
  const [linePreviews, setLinePreviews] = useState<LinePreview[]>([]);
  const [groupWarnings, setGroupWarnings] = useState<string[]>([]);
  const [sendEmailReport, setSendEmailReport] = useState(true);
  const [restoreResult, setRestoreResult] = useState<RestoreCancelledContractsResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setRestoreResult(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const histories = await mapWithConcurrencyLimit(
          selectedContracts,
          RESTORE_PARALLELISM_LIMIT,
          async (contract) => {
            const history = await contractService.getContractStatusHistory(contract.id);
            return { contractId: contract.id, history };
          }
        );

        const mapped = selectedContracts.map((contract) => {
          const contractHistory = histories.find((h) => h.contractId === contract.id)?.history ?? [];
          const normalizedCurrentStatus = normalizeContractStatus(contract.status);
          const latestCancelled = contractHistory
            .filter((h) => normalizeContractStatus(h.status) === EContractStatus.Cancelado)
            .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())[0];

          let eligible = true;
          let reason: string | undefined;
          if (normalizedCurrentStatus !== EContractStatus.Cancelado) {
            eligible = false;
            reason = 'Contrato não está cancelado.';
          } else if (!latestCancelled?.changedAt) {
            eligible = false;
            reason = 'Histórico de cancelamento não encontrado.';
          } else {
            const cancelledAt = new Date(latestCancelled.changedAt).getTime();
            const windowStart = getRestoreWindowStartMs();
            if (cancelledAt < windowStart) {
              eligible = false;
              reason = getRestoreWindowExpiredReason();
            }
          }

          const previousStatus =
            latestCancelled?.previousStatus != null
              ? normalizeContractStatus(latestCancelled.previousStatus)
              : null;

          return {
            contract,
            previousStatus,
            selectedStatus: resolveInitialRestoreStatus(previousStatus, isAdmin),
            eligible,
            reason,
          };
        });

        const warnings: string[] = [];
        const selectedIds = new Set(selectedContracts.map((c) => c.id));
        const groupIds = Array.from(
          new Set(selectedContracts.map((c) => c.groupId).filter((id): id is string => Boolean(id)))
        );

        await mapWithConcurrencyLimit(
          groupIds,
          RESTORE_PARALLELISM_LIMIT,
          async (groupId) => {
            const members = await contractService.getContractsByGroupId(groupId);
            const missingCancelled = members.filter(
              (m) =>
                !selectedIds.has(m.id) &&
                normalizeContractStatus(m.status) === EContractStatus.Cancelado
            );
            if (missingCancelled.length > 0) {
              warnings.push(
                `Grupo ${groupId}: ${missingCancelled.length} contrato(s) cancelado(s) não selecionado(s).`
              );
            }
          }
        );

        if (cancelled) return;
        setTargets(mapped);
        setGroupWarnings(warnings);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar preview de restauração.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, selectedContracts, isAdmin]);

  const eligibleTargets = useMemo(() => targets.filter((x) => x.eligible), [targets]);

  const loadLinePreviews = async () => {
    setLoading(true);
    try {
      const details = await mapWithConcurrencyLimit(
        eligibleTargets,
        RESTORE_PARALLELISM_LIMIT,
        async (target) => contractService.getContractById(target.contract.id)
      );
      const lines: LinePreview[] = [];
      details.forEach((contract: ContractResponseDto, idx) => {
        const contractId = eligibleTargets[idx].contract.id;
        (contract.linhas ?? [])
          .filter((l) => !l.excluirDoContrato)
          .forEach((l) => {
            lines.push({
              contractId,
              lineId: l.id,
              chipCcid: l.chipCcid ?? null,
              ddd: l.ddd ?? null,
              numero: l.numero ?? null,
            });
          });
      });
      setLinePreviews(lines);
      setStep(2);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar linhas/ICCIDs.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (
    contractId: string,
    status: EContractStatus,
    previousStatus: EContractStatus | null
  ) => {
    const allowed = getAllowedRestoreStatuses(previousStatus, isAdmin);
    if (!allowed.includes(status)) return;
    setTargets((prev) =>
      prev.map((target) =>
        target.contract.id === contractId ? { ...target, selectedStatus: status } : target
      )
    );
  };

  const applyPreviousStatusToAll = () => {
    setTargets((prev) =>
      prev.map((target) => {
        if (!target.eligible || target.previousStatus == null) return target;
        const allowed = getAllowedRestoreStatuses(target.previousStatus, isAdmin);
        if (!allowed.includes(target.previousStatus)) return target;
        return { ...target, selectedStatus: target.previousStatus };
      })
    );
  };

  const handleRestore = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const statusOverrides = eligibleTargets
        .filter((t) => t.selectedStatus !== null && t.selectedStatus !== t.previousStatus)
        .map((t) => ({
          contractId: t.contract.id,
          status: t.selectedStatus as EContractStatus,
        }));

      const result = await contractService.restoreCancelledContracts({
        contractIds: eligibleTargets.map((t) => t.contract.id),
        statusOverrides,
        sendEmailReport,
      });

      setRestoreResult(result);
      setStep('result');
      onCompleted(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao restaurar contratos.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!restoreResult) return;
    try {
      await contractService.exportRestoreCancelledReport(restoreResult);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao baixar relatório.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeOnDimmerClick={!submitting} className="contract-rollback-modal">
      <style>{`
        .contract-rollback-modal.ui.modal { overflow: visible !important; }
        .contract-rollback-modal > .content { overflow: visible !important; }
        .contract-rollback-modal .ui.dropdown.active .menu.visible { z-index: 100051 !important; }
      `}</style>
      <Modal.Header>
        <Icon name="undo" />
        Restaurar contratos cancelados
        {step !== 'result' && (
          <span style={{ fontSize: '0.9rem', color: '#666', marginLeft: 12 }}>
            Passo {step} de 3
          </span>
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        {error && (
          <Message negative>
            <Message.Header>Não foi possível processar</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {loading ? (
          <Loader active inline="centered" content="Carregando..." />
        ) : step === 1 ? (
          <>
            <Message info>
              Revise os status antes de continuar. Por padrão usamos o <strong>status anterior</strong>.
              A restauração automática só é permitida para cancelamentos dos últimos{' '}
              <strong>90 dias</strong>.
            </Message>
            {groupWarnings.map((w) => (
              <Message warning key={w} size="small" content={w} />
            ))}
            <Button size="small" onClick={applyPreviousStatusToAll} style={{ marginBottom: 12 }}>
              Aplicar status anterior a todos
            </Button>
            <Table celled compact="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Cliente</Table.HeaderCell>
                  <Table.HeaderCell>Tipo</Table.HeaderCell>
                  <Table.HeaderCell>Status atual</Table.HeaderCell>
                  <Table.HeaderCell>Anterior</Table.HeaderCell>
                  <Table.HeaderCell>Restaurar para</Table.HeaderCell>
                  <Table.HeaderCell>Elegível</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {targets.map((target) => (
                  <Table.Row key={target.contract.id} disabled={!target.eligible}>
                    <Table.Cell>
                      <strong>{target.contract.clientRazaoSocial}</strong>
                      <div style={{ color: '#666', fontSize: '0.85rem' }}>{formatCnpj(target.contract.clientCnpj)}</div>
                    </Table.Cell>
                    <Table.Cell>{target.contract.tipoContratacao || '-'}</Table.Cell>
                    <Table.Cell>{getContractStatusText(target.contract.status)}</Table.Cell>
                    <Table.Cell>
                      {target.previousStatus != null ? getContractStatusText(target.previousStatus) : '-'}
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 220 }}>
                      <Dropdown
                        fluid
                        selection
                        search
                        scrolling
                        disabled={!target.eligible}
                        options={getRestoreStatusOptions(target.previousStatus, isAdmin)}
                        value={target.selectedStatus ?? undefined}
                        onChange={(_, d) =>
                          handleStatusChange(
                            target.contract.id,
                            d.value as EContractStatus,
                            target.previousStatus
                          )
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      {target.eligible ? (
                        <span style={{ color: '#21ba45' }}>Sim</span>
                      ) : (
                        <span style={{ color: '#db2828' }}>{target.reason ?? 'Não'}</span>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </>
        ) : step === 2 ? (
          <>
            <Message info>
              Preview das linhas e ICCIDs que serão reatribuídos quando possível (original ou substituto do mesmo cluster).
            </Message>
            {linePreviews.length > 0 && (
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 6 }}>
                {linePreviews.length} linha(s) encontrada(s)
              </div>
            )}
            <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4 }}>
              <Table celled compact size="small" style={{ margin: 0 }}>
                <Table.Header style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <Table.Row>
                    <Table.HeaderCell>Contrato</Table.HeaderCell>
                    <Table.HeaderCell>Linha</Table.HeaderCell>
                    <Table.HeaderCell>DDD/Número</Table.HeaderCell>
                    <Table.HeaderCell>ICCID anterior</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {linePreviews.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={4}>Nenhuma linha com ICCID para preview.</Table.Cell>
                    </Table.Row>
                  ) : (
                    linePreviews.map((line) => (
                      <Table.Row key={line.lineId}>
                        <Table.Cell>
                          <code>{line.contractId}</code>
                        </Table.Cell>
                        <Table.Cell>
                          <code>{line.lineId}</code>
                        </Table.Cell>
                        <Table.Cell>
                          {line.ddd ?? '—'} / {line.numero ?? '—'}
                        </Table.Cell>
                        <Table.Cell>{line.chipCcid ? <code>{line.chipCcid}</code> : '—'}</Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            </div>
          </>
        ) : step === 3 ? (
          <>
            <Message warning>
              Confirme a restauração de <strong>{eligibleTargets.length}</strong> contrato(s).
            </Message>
            <Checkbox
              label="Enviar relatório por e-mail"
              checked={sendEmailReport}
              onChange={(_, d) => setSendEmailReport(Boolean(d.checked))}
            />
          </>
        ) : restoreResult ? (
          <>
            <Message
              positive={restoreResult.failedContracts === 0}
              warning={restoreResult.failedContracts > 0 && restoreResult.restoredContracts > 0}
              negative={restoreResult.restoredContracts === 0}
            >
              <Message.Header>Restauração concluída</Message.Header>
              <p>
                Restaurados: <strong>{restoreResult.restoredContracts}</strong> · Falhas:{' '}
                <strong>{restoreResult.failedContracts}</strong>
              </p>
            </Message>
            <Table celled compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Contrato</Table.HeaderCell>
                  <Table.HeaderCell>Cliente</Table.HeaderCell>
                  <Table.HeaderCell>Resultado</Table.HeaderCell>
                  <Table.HeaderCell>Linhas / ICCID</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {restoreResult.contracts.map((item) => (
                  <Table.Row key={item.contractId}>
                    <Table.Cell>
                      <code>{item.contractId}</code>
                    </Table.Cell>
                    <Table.Cell>{item.clientRazaoSocial}</Table.Cell>
                    <Table.Cell>
                      {item.restored ? (
                        <span style={{ color: '#21ba45' }}>OK</span>
                      ) : (
                        <span style={{ color: '#db2828' }}>{item.message}</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {item.lines.map((l) => (
                        <div key={l.lineId} style={{ fontSize: '0.85rem' }}>
                          {l.outcome}: {l.previousIccid ?? '—'} → {l.currentIccid ?? '—'}
                        </div>
                      ))}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <Button icon labelPosition="left" onClick={() => void handleDownloadReport()}>
              <Icon name="file excel outline" />
              Baixar XLSX
            </Button>
          </>
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        {step === 'result' ? (
          <Button primary onClick={onClose}>
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            {step > 1 && (
              <Button onClick={() => setStep((step - 1) as 1 | 2)} disabled={submitting}>
                Voltar
              </Button>
            )}
            {step === 1 && (
              <Button
                primary
                disabled={eligibleTargets.length === 0 || loading}
                onClick={() => void loadLinePreviews()}
              >
                Próximo: ICCIDs
              </Button>
            )}
            {step === 2 && (
              <Button primary onClick={() => setStep(3)}>
                Próximo: Confirmar
              </Button>
            )}
            {step === 3 && (
              <Button primary loading={submitting} onClick={() => void handleRestore()} icon labelPosition="left">
                <Icon name="check" />
                Confirmar restauração
              </Button>
            )}
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default ContractRollbackPreviewModal;
